/**
 * useThemePreferences Hook
 *
 * Manages theme and logo preferences with localStorage persistence.
 * Extracted from AppContent.js as part of Phase 1 refactoring.
 *
 * Features:
 * - Theme management (dark-mode / light-mode)
 * - Logo preference (schlage / romanworks)
 * - Automatic localStorage persistence via Zustand
 * - Body class application for theme
 *
 * @returns {Object} Theme preferences and actions
 */

import { useEffect } from "react";
import { useUIStore } from "../stores";
import logger from "../utils/logger";

export const useThemePreferences = () => {
  // Get theme state from UIStore (already persisted)
  const theme = useUIStore((state) => state.theme);
  const setTheme = useUIStore((state) => state.setTheme);
  const activeLogo = useUIStore((state) => state.activeLogo);
  const setActiveLogo = useUIStore((state) => state.setActiveLogo);

  // Apply theme to body class
  useEffect(() => {
    document.body.className = theme;
    logger.log(`[useThemePreferences] Theme applied: ${theme}`);
  }, [theme]);

  // Toggle functions
  const toggleTheme = () => {
    const newTheme = theme === "dark-mode" ? "light-mode" : "dark-mode";
    setTheme(newTheme);
    logger.log(`[useThemePreferences] Theme toggled to: ${newTheme}`);
  };

  const toggleLogo = () => {
    const newLogo = activeLogo === "schlage" ? "romanworks" : "schlage";
    setActiveLogo(newLogo);
    logger.log(`[useThemePreferences] Logo toggled to: ${newLogo}`);
  };

  return {
    theme,
    setTheme,
    toggleTheme,
    activeLogo,
    setActiveLogo,
    toggleLogo,
  };
};
